import React, { useContext, useEffect, useState } from 'react';
import {
  SitecoreContext,
  withSitecoreContext,
  Field,
  WithSitecoreContextProps,
} from '@sitecore-jss/sitecore-jss-react';
import ToastNotification from '../ToastNotification/ToastNotification';
import './index.scss';
import Button from '@mui/material/Button';
import { Navigate } from 'react-router-dom';
import { postAPIAsyncCall, postAPICall } from '../../dataFetcher/axiosMethodCalls';
import { APIWebstoreContext } from '../../context/globalContext';
import LoadingSection from '../LoadingSection/LoadingSection';
import { ProcessOrderValidationService } from '../CheckoutPage/ProcessOrderValidationService';
import LoadingIcon from '../LoadingIcon/LoadingIcon';
import PaymentErrors from './PaymentErrors';
import Modal from '@mui/material/Modal';
import { DialogActions, DialogContent, DialogTitle } from '../DialogComponent/DialogComponent';
import { CartItemsDetails } from '../Product/StandardProductCartDetails/StandardProductCartDetails';
import { OrderAnalyticsInfo, purchaseEvent } from '../../utils/analyticsHelper';
import { UnifiedCartDataService } from '../../utils/UnifiedCartDataService';
import { STATUS_OK, STATUS_REDIRECT, VERIFONE_PAYMENT_IDENTIFIER } from '../../utils/constants';
import {
  CountryFieldProps,
  CalculateErrors,
} from '../Order/OrderSummaryCheckout/OrderSummaryCheckout';
import { OrderSummaryDetails } from '../Order/OrderSummary/OrderSummary';
import { LOB_LIST, OrderDetails } from '../../context/reducer';
import { getIdpToken } from '../../utils/authHelper';
import { PaymentTypes } from '../../utils/PaymentTypes';
import { SubmitProcessValidationService } from '../SubmitPaymentProcess/SubmitProcessValidationService';
export interface ErrorResponse {
  errorCode: string;
  errorMessage: string;
}

export interface IPayment {
  AccountNumber: string;
  CardType: string;
  ExpirationDate: string;
  CVV: string;
  CardholderName: string;
}

export interface OrderResponse {
  error: ErrorResponse;
  orderId: string;
  responseStatus: string;
  synced: boolean;
  stateChanged: boolean;
  isGroupInvoice: boolean;
}

export interface SyncOrderResponse {
  orderResponse: OrderResponse[];
  unifiedReferenceNumber: string;
  synced: boolean;
  stateChanged: boolean;
  responseStatus: string;
  error: ErrorResponse;
}

type PlaceOrderProcessProps = WithSitecoreContextProps & {
  fields: {
    ToastMessage: Field<string>;
    ToastMessagePayFabric: Field<string>;
    TaxExemptPendingText: Field<string>;
    TaxExemptProceedText: Field<string>;
    TaxExemptReturnText: Field<string>;
  };
  isPrivacyPolicyAgreed: boolean;
  isOrderLoading: boolean;
  sitecoreContext: SitecoreContext & { pageEditing: boolean };
  taxExemptPopUp?: boolean;
  taxExemptPending?: boolean;
  orderSummaryDetails: {
    [lob: string]: OrderSummaryDetails;
  };
  orderSummaryBtnText?: string;
  language?: string;
  restrictedCountries?: Array<CountryFieldProps>;
  orderTotal?: number;
  calculateErrors: CalculateErrors[];
};

export type OrderPaymentRequest = {
  orderId: string;
  PaymentMethod: string;
};

export interface Orders {
  [lob: string]: OrderDetails;
}

const PlaceOrderProcess = (props: PlaceOrderProcessProps): React.JSX.Element => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const { state, dispatch } = useContext(APIWebstoreContext);
  const [navigateOnOrderSubmit, setNavigateOnOrderSubmit] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState<Field<string>>();
  const [customToastMessage] = useState(props.fields.ToastMessage);
  const [confirmPopup, setConfirmPopup] = useState(false);
  const [taxExemptPopup, setTaxExemptPopup] = useState(false);
  const [orderPaymentRequest, setOrderPaymentRequest] = useState<OrderPaymentRequest[]>([]);

  function formJSONAndPushToGTM(orderDetails: Orders) {
    Object.keys(orderDetails).forEach((lob) => {
      const cartInfo: Array<CartItemsDetails> =
        UnifiedCartDataService.getCartInfo()[lob]?.cartInfo ?? [];

      const lobOrderDetails = orderDetails[lob];
      const orderIds = lobOrderDetails?.orderIds;

      orderIds?.forEach((orderId) => {
        const orderSummaryDetails = props?.orderSummaryDetails[lob];

        let value: any = orderSummaryDetails.total;
        value = value.toFixed(2);

        const orderAnalyticsInfo: OrderAnalyticsInfo = {
          transaction_id: orderId,
          tax: orderSummaryDetails?.taxTotal.toFixed(2),
          shipping: orderSummaryDetails?.shippingTotal.toFixed(2),
          value: value,
          currency: 'USD',
          payment_type: lobOrderDetails?.paymentType,
          items: cartInfo.map((item: CartItemsDetails) => {
            let price: any = item?.priceOption?.unitPrice;
            price = price.toFixed(2);
            return {
              item_id: `${item?.product?.productId}`,
              item_name: `${item?.product?.title}`,
              item_brand: `${item?.product?.productType}`,
              item_variant: item?.priceOption?.variantId,
              price: price,
              quantity: item?.priceOption?.quantity,
            };
          }),
        };

        purchaseEvent(orderAnalyticsInfo);
      });
    });
  }

  const toastRemove = () => {
    setShowToast(false);
  };

  useEffect(() => {
    if (props?.taxExemptPending) {
      showCustomToast(
        state?.sitecoreDictionaryData
          ? state?.sitecoreDictionaryData.TaxExemptPendingMessage
          : 'Your checkout is disabled until tax exempt verification is completed. It takes us about 2-3 days to get the verification completed'
      );
    }
  }, []);

  function placeOrderProcessStatus(isLoading: boolean) {
    if (isLoading) {
      return (
        <LoadingSection
          open={isLoading}
          message="Please do not go back or close the window or hit the button again"
        />
      );
    } else {
      return <div></div>;
    }
  }

  const showCustomToast = (message: string) => {
    customToastMessage.value = message;
    setToastMessage(customToastMessage);
    setShowToast(true);
  };

  const validateShippingAddress = async (data: any) => {
    let APIResponse: any = {};
    let statusResponse: any;
    let addressValidationStatusCode: any;

    const validateShippingAddressRequest = {
      city: data.city,
      country: data.country,
      line1: data.street1,
      line2: data.street2,
      postalCode: data.zip,
      region: data.state,
    };
    try {
      APIResponse = await postAPICall(
        `${process.env.REACT_APP_PAYMENT_URL}/address/ValidateAddress`,
        validateShippingAddressRequest
      );
    } catch {
      return { statusResponse: false, addressValidationStatusCode };
    }
    if (data.country === 'US' || data.country === 'CA') {
      statusResponse = APIResponse.data.status;
      addressValidationStatusCode = undefined;
    } else {
      statusResponse = APIResponse.data.status;
      addressValidationStatusCode = APIResponse.data.statusCode;
    }

    return { statusResponse, addressValidationStatusCode };
  };

  function hasBillingAddressErrors(billAddress: any) {
    if (
      billAddress.country.length < 2 ||
      billAddress.city === '' ||
      billAddress.firstName === '' ||
      billAddress.lastName === '' ||
      billAddress.postal === '' ||
      billAddress.state === '' ||
      billAddress.street === ''
    ) {
      return state?.sitecoreDictionaryData
        ? state?.sitecoreDictionaryData.BillingAddressErrorMessage
        : 'Billing Address Error';
    } else {
      return '';
    }
  }

  function renderTaxExemptPopup() {
    return (
      <Modal
        open={taxExemptPopup}
        onClose={(_event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
          if (reason !== 'backdropClick') {
            setTaxExemptPopup(false);
          }
        }}
        className="ws-modal-dialog"
      >
        <div className="taxExemptDialog taxExemptDialogContent">
          <DialogTitle id="customized-dialog-title" onClose={() => setTaxExemptPopup(false)}>
            Tax Exemption Confirmation
          </DialogTitle>
          <DialogContent dividers>{props?.fields?.TaxExemptPendingText?.value}</DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setTaxExemptPopup(false);
                processOrder();
              }}
              className="ws-primary-button-no-border"
            >
              {props?.fields?.TaxExemptProceedText?.value}
            </Button>
            <Button
              onClick={() => {
                setTaxExemptPopup(false);
                window.location.href = '/carts';
              }}
              className="ws-white-button"
            >
              {props?.fields?.TaxExemptReturnText?.value}
            </Button>
          </DialogActions>
        </div>
      </Modal>
    );
  }

  const handlePopupYes = () => {
    setConfirmPopup(false);
    submitOrderProcess(orderPaymentRequest, false);
  };

  const handleConfirmPopupClose = () => {
    setConfirmPopup(false);
  };

  function hasPOBoxAddress(shippingInfo: any) {
    const poBoxRegex =
      /\b(P\.O\.|PO|P O|P\.O|P O\.|PO\.)\s*(BOX|BOX\.)?\s*(\:|\-)?\s*(No|No\.|\#)?\s*\d+/i;
    return shippingInfo.street1.length > 0 && shippingInfo.street1.match(poBoxRegex) ? true : false;
  }

  function openAddressValidationPopup(openDialog: boolean) {
    return (
      <Modal
        open={openDialog}
        onClose={(_event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
          if (reason !== 'backdropClick') {
            handleConfirmPopupClose();
          }
        }}
        className="ws-modal-dialog"
      >
        <div className="addressValidationDialog addressValidationDialogPaper">
          <DialogTitle id="customized-dialog-title" onClose={handleConfirmPopupClose}>
            Address Validation
          </DialogTitle>
          <DialogContent dividers>
            {state?.sitecoreDictionaryData &&
            state?.sitecoreDictionaryData?.InvalidAddressConfirmation
              ? state?.sitecoreDictionaryData.InvalidAddressConfirmation
              : 'Unable to validate your address. Would you like to place the order without validating the address?'}
          </DialogContent>
          <DialogActions>
            <Button onClick={handlePopupYes} className="ws-primary-button-no-border">
              YES
            </Button>
            <Button onClick={handleConfirmPopupClose} className="ws-white-button">
              NO
            </Button>
          </DialogActions>
        </div>
      </Modal>
    );
  }

  async function processOrder() {
    const OrderRequest: OrderPaymentRequest[] = [];
    if (state?.orderDetails) {
      for (const lob of LOB_LIST) {
        const details = state.orderDetails[lob];
        const { paymentType, orderIds } = details;
        for (const orderId of orderIds) {
          OrderRequest.push({
            orderId,
            PaymentMethod: paymentType,
          });
        }
      }

      setOrderPaymentRequest(OrderRequest);
    }

    if (isDisabled) {
      return;
    }

    setIsDisabled(true);

    setTimeout(() => {
      setIsDisabled(false);
    }, 2000);

    let addressNotValidated = false;
    let addressValidated = false;

    if (state?.syncFailedCheckoutLOBs?.length) {
      const syncFailedCheckoutLOBs = state.syncFailedCheckoutLOBs.join(', ');
      showCustomToast(
        state?.sitecoreDictionaryData
          ? state?.sitecoreDictionaryData?.SyncOrderFailureMessage?.replace(
              '##LOB##',
              syncFailedCheckoutLOBs
            )
          : `It looks like the ${syncFailedCheckoutLOBs} order(s) may have changed. Please remove and re-add the items to proceed.`
      );
      return;
    }

    if (props?.calculateErrors?.length > 0) {
      for (const error of props.calculateErrors) {
        if (error.errorCode === 'OrderCalculate.CalculateConfig') {
          showCustomToast(
            state?.sitecoreDictionaryData
              ? state.sitecoreDictionaryData.MissingShippingInfoErrorMessage
              : 'Missing Shipping information! Please add Shipping address to continue checkout.'
          );
          return;
        } else if (error.errorCode === 'OrderCalculate.TaxError') {
          showCustomToast(
            state?.sitecoreDictionaryData
              ? state.sitecoreDictionaryData.AddressTaxValidationError
              : 'We are unable to validate your address. Please add or change the correct address to proceed checkout!'
          );
          return;
        } else if (error.errorCode === 'OrderCalculate.TaxObjectError') {
          showCustomToast(
            state?.sitecoreDictionaryData
              ? state.sitecoreDictionaryData.AddressNotSetError
              : 'We are unable to set the shipping address for your order. Please refresh the page!'
          );
          return;
        } else {
          showCustomToast(
            error.errorMessage ??
              'We are unable to validate your address. Please add or change the correct address to proceed checkout!'
          );
          return;
        }
      }
    }

    if (state?.paymentType === PaymentTypes.CreditCard) {
      if (dispatch) {
        dispatch({
          type: 'CHECKOUT_UPDATE',
          payload: {
            toggleProcessOrderValidation: !state?.toggleProcessOrderValidation,
          },
        });
      }
    }

    let shippingInfo = ProcessOrderValidationService.getShippingInfo().shippingInfo;

    if (
      ProcessOrderValidationService.getShippingInfo().shippingInfo &&
      ProcessOrderValidationService.getShippingInfo().shippingInfo.toString() === '[object Object]'
    ) {
      try {
        if (
          shippingInfo &&
          (shippingInfo.country === 'CN' ||
            shippingInfo.country === String.raw`People's Republic of China`) &&
          state?.deliveryStatus?.includes('Print')
        ) {
          showCustomToast(
            state?.sitecoreDictionaryData &&
              state?.sitecoreDictionaryData.ChinaUserPrintProductNotification
              ? state?.sitecoreDictionaryData.ChinaUserPrintProductNotification
              : 'Shipping is unavailable in your region.  Please remove any print products and replace with PDF products in your cart to continue with your purchase.'
          );
          return;
        }

        if (
          shippingInfo &&
          (shippingInfo.country === 'CN' ||
            shippingInfo.country === String.raw`People's Republic of China`) &&
          state?.hasStatisticsReport
        ) {
          showCustomToast(
            state?.sitecoreDictionaryData &&
              state?.sitecoreDictionaryData.StatisticsReportChineseError
              ? state?.sitecoreDictionaryData.StatisticsReportChineseError
              : 'Statistics Reports are not purchasable in your region. Please remove the statistics reports from your cart if any and then place your order again'
          );
          return;
        }

        if (
          shippingInfo &&
          ('' + shippingInfo.phone).replace('undefined', '').replace('null', '').trim() === '' &&
          state?.deliveryStatus?.includes('Print')
        ) {
          showCustomToast(
            state?.sitecoreDictionaryData &&
              state?.sitecoreDictionaryData?.PhoneNumberValidationError
              ? state?.sitecoreDictionaryData.PhoneNumberValidationError
              : 'Phone Number is required for all print orders. Please update the address with phone number.'
          );
          return;
        }

        if (!state?.shipToChangeAllowed) {
          addressValidated = true;
        } else if (shippingInfo) {
          const { statusResponse, addressValidationStatusCode } = await validateShippingAddress(
            shippingInfo
          );

          if (!statusResponse || String(addressValidationStatusCode) === 'I') {
            addressValidated = false;
            // Please follow the link for Informatica Status Codes: https://dev.azure.com/energyapi/eCommerce/_wiki/wikis/eCommerce.wiki/2305/Informatica-Address-Validation
            if (addressValidationStatusCode && String(addressValidationStatusCode) === 'I') {
              addressNotValidated = true;
            } else {
              showCustomToast(
                state?.sitecoreDictionaryData
                  ? state?.sitecoreDictionaryData.AddressValidationError
                  : 'Address Validation Error'
              );
              return;
            }
          } else {
            addressValidated = true;
          }
        } else {
          addressValidated = false;
          showCustomToast(
            state?.sitecoreDictionaryData
              ? state?.sitecoreDictionaryData.AddressValidationError
              : 'Address Validation Error'
          );
          return;
        }
      } catch (error: any) {
        addressValidated = false;
        showCustomToast(
          state?.sitecoreDictionaryData
            ? `${state?.sitecoreDictionaryData.AddressValidationFailureResponseMessage} - ${error?.response?.status} - ${error?.response?.statusText}`
            : 'Address Validation Response Failure Message'
        );
        return;
      }
    } else {
      addressValidated = false;
      showCustomToast(
        state?.sitecoreDictionaryData
          ? state?.sitecoreDictionaryData.MissingShippingInfoErrorMessage
          : 'Missing Shipping Information. Plese verify the details!'
      );
      return;
    }

    if (state?.deliveryStatus?.includes('Print') && hasPOBoxAddress(shippingInfo) === true) {
      showCustomToast(
        state?.sitecoreDictionaryData
          ? state?.sitecoreDictionaryData.AddressValidationPrintOrders
          : 'P.O. Box Address is not accepted for print orders. Please remove P.O. Box address from shipping address and re-submit the order.'
      );
      return;
    }

    if (
      (state?.paymentType === 'None' ||
        OrderRequest.some((order) => order.PaymentMethod === 'None')) &&
      shippingInfo.country !== 'CN' &&
      shippingInfo.country !== String.raw`People's Republic of China` &&
      (props?.orderTotal ?? 0 > 0)
    ) {
      showCustomToast(
        state?.sitecoreDictionaryData
          ? state?.sitecoreDictionaryData.InvalidPaymentMethod
          : 'Please select a payment method'
      );
      return;
    }
    if (!props.isPrivacyPolicyAgreed) {
      showCustomToast(
        state?.sitecoreDictionaryData
          ? state?.sitecoreDictionaryData.EULAErrorMessage
          : 'Please read and accept the EULA to proceed with checkout.'
      );
      return;
    }

    try {
      if (
        (props?.orderTotal ?? 0) > 0 &&
        shippingInfo &&
        (shippingInfo.country === 'CN' ||
          shippingInfo.country === String.raw`People's Republic of China`)
      ) {
        setIsLoading(true);
        await redirectForExternalPaymentProcessing(OrderRequest);
        return;
      }
    } catch (error: any) {
      showCustomToast(
        state?.sitecoreDictionaryData
          ? state?.sitecoreDictionaryData?.CheckoutExternalPaymentRedirectCatchError
          : 'Order Processing Failed. Please retry.'
      );
      setIsLoading(false);
      return;
    }

    if (state?.paymentType === undefined || state?.paymentType?.length === 0) {
      showCustomToast(
        state?.sitecoreDictionaryData
          ? state?.sitecoreDictionaryData.PayFabricErrorMessage
          : 'Payment failure. Please verify the details.'
      );
      return;
    }

    if (state?.paymentType === PaymentTypes.CreditCard) {
      if (
        ProcessOrderValidationService.getErrorMessage().text &&
        ProcessOrderValidationService.getErrorMessage().text !== ''
      ) {
        showCustomToast('' + ProcessOrderValidationService.getErrorMessage().text);
        return;
      }

      if (
        ProcessOrderValidationService.getBillingInfo().billingInfo.toString() !== '[object Object]'
      ) {
        let billingInfo = ProcessOrderValidationService.getBillingInfo().billingInfo();

        if (Object.keys(billingInfo).length > 0) {
          let errorMessage = hasBillingAddressErrors(billingInfo);

          if (errorMessage.length > 0) {
            showCustomToast(errorMessage);
            return;
          }
        }
      }
    } else {
      const errorMessageText = SubmitProcessValidationService.getErrorMessage().text;
      if (errorMessageText && errorMessageText !== '') {
        showCustomToast(errorMessageText);
        setIsDisabled(false);
        return;
      }
    }

    if (addressNotValidated) {
      setConfirmPopup(true);
      return;
    }

    submitOrderProcess(OrderRequest, addressValidated);
  }

  async function submitOrderProcess(
    orderRequest: OrderPaymentRequest[],
    addressValidated: boolean
  ) {
    setIsLoading(true);

    let response: any = [];
    try {
      response = await calculateOrder(orderRequest.map((request) => request.orderId));
      if (response) {
        for (const data of response.data) {
          if (data.errors && data.errors.length > 0) {
            if (
              data.errors.some(
                (error: CalculateErrors) => error.errorCode === 'OrderCalculate.TaxError'
              )
            ) {
              showCustomToast(
                state?.sitecoreDictionaryData?.AddressTaxValidationError ??
                  'We are unable to validate your address. Please add or change the correct address to proceed checkout!'
              );
              setIsLoading(false);
              return;
            } else {
              for (const error of data.errors) {
                showCustomToast(
                  error?.errorMessage ??
                    state?.sitecoreDictionaryData?.OrderCalculationErrorMessage ??
                    'Order Calculation Failure. Please verify the details and try again!'
                );
                setIsLoading(false);
                return;
              }
            }
          }
          if (response && response.data) {
            const orderTotal = Object.values(props?.orderSummaryDetails).reduce(
              (sum, detail) => sum + detail.total,
              0
            );

            const responseTotal = response.data.reduce(
              (sum: number, item: any) => sum + item.total,
              0
            );

            if (orderTotal !== responseTotal) {
              showCustomToast(
                state?.sitecoreDictionaryData
                  ? state?.sitecoreDictionaryData.OrderCalculationErrorMessage
                  : 'Order Calculation Failure. Please verify the details and try again!'
              );
              setIsLoading(false);
              return;
            }
          }
        }
      }
    } catch (error: any) {
      showCustomToast(
        state?.sitecoreDictionaryData
          ? state?.sitecoreDictionaryData.OrderCalculationErrorMessage
          : 'Order Calculation Failure. Please verify the details and try again!'
      );
      setIsLoading(false);
      return;
    }

    submitOrder(orderRequest, addressValidated);
  }

  const calculateOrder = async (orderids: string[]) => {
    const response = await postAPIAsyncCall(
      `${process.env.REACT_APP_CART_URL}/checkout/calculate`,
      orderids
    );

    return response;
  };

  function findDebitCardType(ccNumber: string) {
    const regexPattern: { [key: string]: RegExp } = {
      MASTER_CARD: /^5[1-5][0-9]{1,}|^2[2-7][0-9]{1,}$/,
      VISA: /^4[0-9]{2,}$/,
      AMERICAN_EXPRESS: /^3[47][0-9]{5,}$/,
      UNION_PAY: /^(62[0-9]{14,17})$/,
    };

    for (const item in regexPattern) {
      if (ccNumber.replace(/[^\d]/g, '').match(regexPattern[item])) {
        return item;
      }
    }
    return '';
  }

  function submitOrder(orderRequest: OrderPaymentRequest[], addressValidated: boolean) {
    let billingInfo = null;
    let isSameAsShipping = true;
    const orderDetails = state?.orderDetails;

    let payment: IPayment | null = null;

    if (state?.paymentType === PaymentTypes.CreditCard) {
      isSameAsShipping = ProcessOrderValidationService.getBillingSameAsShipping().sameAsShipping;
      if (!isSameAsShipping) {
        let billData = ProcessOrderValidationService.getBillingInfo().billingInfo();
        billingInfo = {
          FirstName: billData.firstName,
          LastName: billData.lastName,
          Street1: billData.street,
          Street2: billData.unit,
          City: billData.city,
          State: billData.state,
          Zip: billData.postal,
          Country: billData.country,
          Phone: billData.phone,
        };
      }

      let ccInfoData = ProcessOrderValidationService.getCCInfo();
      let cardType = findDebitCardType(ccInfoData.ccinfo.card);
      payment = {
        AccountNumber: ccInfoData.ccinfo.card,
        CardType: cardType,
        ExpirationDate: ccInfoData.ccinfo.expiry,
        CVV: ccInfoData.ccinfo.securityCode,
        CardholderName: ccInfoData.ccinfo.cardHolder,
      };
    }

    var paymentDetails = SubmitProcessValidationService.getNonInstantPaymentInfo();

    let submitOrderRequest = {
      orders: orderRequest,
      Payment: payment,
      BillingIsSameAsShippingAddress: isSameAsShipping,
      BillingAddress: billingInfo,
      AddressValidated: addressValidated,
      GroupNumber: state?.groupNumber,
      PurchaseOrderPaymentDetails: paymentDetails.paymentInfo,
    };
    const idpToken = getIdpToken();

    postAPICall(`${process.env.REACT_APP_CART_URL}/checkout/submit`, submitOrderRequest, {
      headers: {
        'B2C-Authorization': idpToken,
      },
    })
      .then(function (response: any) {
        if (response && response.status === 200) {
          processOrderResponse(response?.data);
        }
      })
      .catch(function (error) {
        // handle error
        if (error?.response?.data) {
          processOrderResponse(error?.response?.data);
        }
      });

    try {
      if (orderDetails !== undefined) {
        formJSONAndPushToGTM(orderDetails);
      }
    } catch (error: any) {
      console.log(error);
    }
  }

  function processOrderResponse(orderResponse: SyncOrderResponse) {
    if (orderResponse) {
      if (dispatch) {
        var toggleOrderSummaryLoadValue =
          orderResponse.responseStatus === 'Confirmed' || orderResponse.responseStatus === 'Pending'
            ? state?.toggleOrderSummaryLoad
            : !state?.toggleOrderSummaryLoad;
        dispatch({
          type: 'CART_UPDATE',
          payload: {
            toggleCartQuantityLoad: !state?.toggleCartQuantityLoad,
            lastOrderId: orderResponse.unifiedReferenceNumber,
            toggleOrderSummaryLoad: toggleOrderSummaryLoadValue,
          },
        });
      }
      switch (orderResponse.responseStatus) {
        case 'Confirmed':
          setNavigateOnOrderSubmit('/orders/confirmation');
          break;
        case 'Pending':
          setNavigateOnOrderSubmit('/orders/pending');
          break;
        case 'Failed':
          if (orderResponse.error && orderResponse.error.errorCode === 'ServerError') {
            if (orderResponse.synced === false) {
              showCustomToast(
                state?.sitecoreDictionaryData
                  ? state?.sitecoreDictionaryData?.SyncOrderFailureMessage
                  : 'It looks like the ICP order may have changed. Please remove and re-add the items to proceed.'
              );
            } else if (orderResponse.stateChanged === true) {
              showCustomToast(
                state?.sitecoreDictionaryData
                  ? state?.sitecoreDictionaryData?.StateChangedToastMessage
                  : 'Looks like the cart has been updated! Please refresh to proceed!'
              );
            } else {
              setNavigateOnOrderSubmit('/orders/unsuccessful');
            }
          } else if (
            orderResponse &&
            orderResponse.error &&
            orderResponse.error.errorCode === 'OrderSubmit.CCPaymentAlreadyPaid'
          ) {
            showCustomToast(
              state?.sitecoreDictionaryData
                ? state?.sitecoreDictionaryData.CCPaymentAlreadyPaid
                : 'Unsuccessful Order processing. Payment already submitted using Credit Card. Please choose the same payment method or contact support for further assistance.'
            );
          } else if (orderResponse && orderResponse.error && orderResponse.error.errorCode !== '') {
            let errorKey = Object.keys(PaymentErrors).findIndex(
              (key) => key === orderResponse.error.errorMessage
            );
            let errorMessage =
              errorKey >= 0
                ? Object.values(PaymentErrors).at(errorKey)
                : orderResponse.error.errorMessage;
            showCustomToast(
              errorMessage || 'Unsuccessful Order processing. Please verify the details!'
            );
          } else {
            showCustomToast(
              state?.sitecoreDictionaryData
                ? state?.sitecoreDictionaryData.OrderUnsuccessfulRetryMessage
                : 'Unsuccessful Order processing. Please verify the details!'
            );
          }
          setIsLoading(false);
          break;
        default:
          showCustomToast(
            state?.sitecoreDictionaryData
              ? state?.sitecoreDictionaryData.OrderUnsuccessfulRetryMessage
              : 'Unsuccessful Order processing. Please verify the details!'
          );
          setIsLoading(false);
      }
    }
  }

  async function redirectForExternalPaymentProcessing(orderRequest: OrderPaymentRequest[]) {
    orderRequest.forEach((order) => {
      if (
        !order.PaymentMethod ||
        order.PaymentMethod === 'None' ||
        Object.values(PaymentTypes).includes(order.PaymentMethod as PaymentTypes)
      ) {
        order.PaymentMethod = VERIFONE_PAYMENT_IDENTIFIER;
      }
    });

    const idpToken = getIdpToken();

    let submitOrderRequest = {
      orders: orderRequest,
      GroupNumber: state?.groupNumber,
      language: props?.language,
    };

    const response = await postAPICall(
      `${process.env.REACT_APP_CART_URL}/external/generateunifiedbuylink`,
      submitOrderRequest,
      {
        headers: {
          'B2C-Authorization': idpToken,
        },
      }
    );

    if (
      response &&
      Number(response.status) >= STATUS_OK &&
      Number(response.status) < STATUS_REDIRECT
    ) {
      window.location.assign(response.headers.location);
    }
  }

  return (
    <div className="placeorderprocess">
      {props.sitecoreContext.pageEditing && (
        <>
          <p>PlaceOrderProcess Component Edit Mode</p>
        </>
      )}

      <div className="placeOrderButton">
        {props.isOrderLoading ? (
          <Button color="secondary" variant="contained" className="checkoutButton">
            <LoadingIcon />
          </Button>
        ) : (
          <Button
            color="secondary"
            variant="contained"
            className={
              props?.taxExemptPending === true ? 'checkoutButtonDisable' : 'checkoutButton'
            }
            title={props?.taxExemptPending === true ? 'Pending tax exempt to place the order' : ''}
            onClick={() => {
              if (props?.taxExemptPopUp) {
                setTaxExemptPopup(true);
              } else {
                processOrder();
              }
            }}
            disabled={props.taxExemptPending || isDisabled}
          >
            <span
              className={
                props?.taxExemptPending === true
                  ? 'checkoutButtonTextDisable'
                  : 'checkoutButtonText'
              }
            >
              {props?.orderSummaryBtnText}
            </span>
          </Button>
        )}
      </div>

      <div>{placeOrderProcessStatus(isLoading)}</div>

      {navigateOnOrderSubmit.length > 1 ? <Navigate to={navigateOnOrderSubmit} replace /> : null}

      <div>{openAddressValidationPopup(confirmPopup)}</div>
      <div>{renderTaxExemptPopup()}</div>

      <ToastNotification
        fields={{
          ToastMessage: toastMessage,
        }}
        showToast={showToast}
        removeToast={toastRemove}
        toastStatus="error"
      ></ToastNotification>
    </div>
  );
};

export default withSitecoreContext()(PlaceOrderProcess);
